// ============================================================
// PERRY — LOGIN OVERLAY
// Email / password against the shared terror-tcg Firebase project,
// with browserLocalPersistence — the same door Terror uses, so one
// Empire account walks into every title.
//
// THE HINT LAW: an arriving ?uid= from Unstable Pierre is an
// EXPECTATION, never a credential. We show it as "welcome back"
// context and then make the player actually authenticate. Every
// rule in the database compares against auth.uid and nothing else.
// ============================================================

import { useState } from "react";
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  setPersistence, browserLocalPersistence,
} from "firebase/auth";
import { auth } from "./firebase";
import { GM } from "./theme";
import { EmberDust, MetalRule, ensureKeyframes } from "./PerryUI";

const KF = `
@keyframes loginIn { from { opacity: 0; transform: translateY(16px) scale(0.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes loginShake { 0%,100% { transform: translateX(0); } 20% { transform: translateX(-7px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(3px); } }
`;

const FRIENDLY = {
  "auth/invalid-email": "That address isn't shaped like an address.",
  "auth/user-not-found": "No such traveller on the ledger. Forge a new one?",
  "auth/wrong-password": "The word doesn't match the name.",
  "auth/invalid-credential": "The name and the word disagree.",
  "auth/email-already-in-use": "That traveller already exists. Sign in instead.",
  "auth/weak-password": "Six characters at minimum. The road is dangerous.",
  "auth/too-many-requests": "Too many attempts. The gate needs a moment.",
};

export default function LoginOverlay({ uidHint }) {
  ensureKeyframes("perry-login-keyframes", KF);
  const [mode, setMode] = useState("in");     // "in" | "new"
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [shakeKey, setShakeKey] = useState(0);

  const submit = async () => {
    if (busy) return;
    setErr("");
    if (!email.trim() || !pw) { setErr("Both fields, please."); setShakeKey((k) => k + 1); return; }
    setBusy(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      if (mode === "in") await signInWithEmailAndPassword(auth, email.trim(), pw);
      else await createUserWithEmailAndPassword(auth, email.trim(), pw);
      // App's onAuthStateChanged takes it from here.
    } catch (e) {
      setErr(FRIENDLY[e?.code] || "The gate refused. Try again.");
      setShakeKey((k) => k + 1);
      setBusy(false);
    }
  };

  const field = {
    width: "100%", padding: "11px 13px", marginTop: 6,
    background: "linear-gradient(175deg, #0d0705, #150c07)",
    border: `1px solid ${GM.pageBorder}`, borderRadius: 8,
    color: GM.vellum, fontFamily: GM.fontUI, fontSize: 14,
    outline: "none", boxShadow: "inset 0 2px 6px rgba(0,0,0,0.7)",
  };
  const label = { fontSize: 10, letterSpacing: 3, color: GM.vellumFaint };

  return (
    <div style={{
      position: "absolute", inset: 0, background: "#050302",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", fontFamily: GM.fontUI,
    }}>
      <div style={{ position: "absolute", inset: 0, background: GM.candlelight }} />
      <EmberDust count={14} />

      <div key={shakeKey} style={{
        position: "relative", width: "min(420px, 88vw)", padding: "30px 30px 26px",
        background: "linear-gradient(175deg, #1c1009 0%, #120a08 60%, #0a0605 100%)",
        border: `1px solid ${GM.pageBorder}`, borderRadius: 14,
        boxShadow: "0 26px 60px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,191,112,0.13)",
        animation: err ? "loginShake 0.4s ease-out" : "loginIn 0.6s cubic-bezier(0.2,0.8,0.2,1)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: 34, letterSpacing: 9, color: GM.vellum,
            textShadow: `0 0 30px ${GM.inkGold}`,
          }}>PERRY</div>
          <MetalRule width={170} style={{ margin: "10px auto 10px" }} />
          <div style={{ fontSize: 10, letterSpacing: 4, color: GM.vellumFaint }}>
            {mode === "in" ? "SIGN IN TO THE EMPIRE" : "FORGE A NEW TRAVELLER"}
          </div>
        </div>

        {uidHint && (
          <div style={{
            marginBottom: 16, padding: "9px 12px", borderRadius: 8,
            background: "rgba(43,184,168,0.07)", border: `1px solid ${GM.amethystDim}`,
            fontSize: 11, color: GM.amethystBright, lineHeight: 1.5,
          }}>
            Arrived from Unstable Pierre. Sign in with the same account and your
            Crests, records and achievements come with you.
          </div>
        )}

        <div style={label}>EMAIL</div>
        <input
          style={field} type="email" value={email} autoComplete="email"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.repeat) submit(); }}
        />

        <div style={{ ...label, marginTop: 15 }}>PASSWORD</div>
        <input
          style={field} type="password" value={pw}
          autoComplete={mode === "in" ? "current-password" : "new-password"}
          onChange={(e) => setPw(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.repeat) submit(); }}
        />

        {err && (
          <div style={{ marginTop: 13, fontSize: 12, color: GM.rose, letterSpacing: 0.4 }}>{err}</div>
        )}

        <button
          onClick={submit}
          disabled={busy}
          style={{
            width: "100%", marginTop: 20, padding: "13px 0",
            background: busy
              ? "linear-gradient(175deg, #2a1a10, #180e08)"
              : "linear-gradient(175deg, #a8601e 0%, #7a4218 55%, #4a2810 100%)",
            border: `1px solid ${GM.inkGold}`, borderRadius: 10,
            color: busy ? GM.vellumFaint : "#ffe6c2",
            fontFamily: GM.font, fontSize: 14, letterSpacing: 5, fontWeight: 700,
            cursor: busy ? "default" : "pointer",
            boxShadow: busy ? "none" : `0 6px 18px rgba(0,0,0,0.6), 0 0 22px ${GM.inkGoldDim}`,
            textShadow: "0 1px 3px rgba(0,0,0,0.8)",
          }}
        >
          {busy ? "…" : mode === "in" ? "ENTER" : "FORGE"}
        </button>

        <div
          onClick={() => { setMode(mode === "in" ? "new" : "in"); setErr(""); }}
          style={{
            marginTop: 16, textAlign: "center", fontSize: 11, letterSpacing: 2,
            color: GM.amethyst, cursor: "pointer",
          }}
        >
          {mode === "in" ? "No account? Forge one →" : "← Already a traveller? Sign in"}
        </div>
      </div>
    </div>
  );
}
